import React, { useEffect, useState, useRef } from 'react';
import './ProgressBar.css';

interface ProgressBarProps {
  current: number;
  total: number;
  message?: string;
  indeterminate?: boolean;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ current, total, message, indeterminate = false }) => {
  const percentage = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
  const [estimatedTime, setEstimatedTime] = useState<string>('');
  const startTimeRef = useRef<number>(Date.now());
  const lastCurrentRef = useRef<number>(0);

  useEffect(() => {
    if (indeterminate) {
      setEstimatedTime('');
      return;
    }

    // Reset timer when starting new load
    if (current === 0 || (current > 0 && lastCurrentRef.current === 0)) {
      startTimeRef.current = Date.now();
      lastCurrentRef.current = current;
      setEstimatedTime('');
      return;
    }

    // Calculate estimated time remaining
    if (current > lastCurrentRef.current && current < total) {
      const elapsedMs = Date.now() - startTimeRef.current;
      const itemsLoaded = current;
      const itemsRemaining = total - current;
      const msPerItem = elapsedMs / itemsLoaded;
      const estimatedRemainingMs = msPerItem * itemsRemaining;

      if (estimatedRemainingMs > 1000) {
        const seconds = Math.ceil(estimatedRemainingMs / 1000);
        if (seconds < 60) {
          setEstimatedTime(`~${seconds}s restantes`);
        } else {
          const minutes = Math.floor(seconds / 60);
          const remainingSeconds = seconds % 60;
          setEstimatedTime(`~${minutes}m ${remainingSeconds}s restantes`);
        }
      } else {
        setEstimatedTime('finalizando...');
      }
    }

    lastCurrentRef.current = current;
  }, [current, total, indeterminate]);
  
  return (
    <div className="progress-bar-container">
      <div className="progress-bar-info">
        <span className="progress-bar-message">{message || 'Carregando...'}</span>
        <div className="progress-bar-details">
          {indeterminate ? (
            <span className="progress-bar-stats">Em andamento...</span>
          ) : (
            <>
              <span className="progress-bar-stats">{current.toLocaleString('pt-BR')} / {total.toLocaleString('pt-BR')} ({percentage}%)</span>
              {estimatedTime && <span className="progress-bar-time">{estimatedTime}</span>}
            </>
          )}
        </div>
      </div>
      <div className="progress-bar-track">
        <div 
          className={`progress-bar-fill${indeterminate ? ' progress-bar-fill--indeterminate' : ''}`} 
          style={{ width: indeterminate ? '45%' : `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export default ProgressBar;
